import React, { useEffect, useState } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { X } from 'lucide-react';
import { postsApi, mediaUrl } from '../../services/postsApi';
import OfficialBadge from './OfficialBadge';

// Keeps every Follow button for the same person in sync across the feed.
export const announceFollow = (uid, on) =>
  window.dispatchEvent(new CustomEvent('lumera:follow-changed', { detail: { uid, on } }));

export const reasonText = (u, es) => {
  switch (u.reason) {
    case 'official': return es ? 'Cuenta oficial' : 'Official account';
    case 'follows_you': return es ? 'Te sigue' : 'Follows you';
    case 'mutual': return es ? `${u.mutual_count} en común` : `Followed by ${u.mutual_count} you follow`;
    case 'church': return es ? 'De tu iglesia' : 'From your church';
    case 'new': return es ? 'Nuevo en Lumera' : 'New to Lumera';
    default: return es ? 'Popular en Lumera' : 'Popular on Lumera';
  }
};

/** Horizontal "Suggested for you" row of follow cards (Instagram-style). */
const SuggestedForYou = ({ language = 'en', onOpenProfile }) => {
  const es = language === 'es';
  const [users, setUsers] = useState([]);
  const [followed, setFollowed] = useState({});

  useEffect(() => { postsApi.suggestions().then(setUsers).catch(() => setUsers([])); }, []);
  useEffect(() => {
    const sync = (e) => setFollowed((f) => ({ ...f, [e.detail.uid]: e.detail.on }));
    window.addEventListener('lumera:follow-changed', sync);
    return () => window.removeEventListener('lumera:follow-changed', sync);
  }, []);

  const toggle = async (uid) => {
    const now = !followed[uid];
    announceFollow(uid, now);
    try { if (now) await postsApi.follow(uid); else await postsApi.unfollow(uid); }
    catch { announceFollow(uid, !now); }
  };

  const visible = users.filter((u) => !u.dismissed);
  if (!visible.length) return null;
  return (
    <div className="rounded-2xl border bg-card p-4" data-testid="suggested-for-you">
      <p className="mb-3 text-sm font-semibold">{es ? 'Sugerencias para ti' : 'Suggested for you'}</p>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {visible.map((u) => (
          <div key={u.user_id} className="relative flex w-36 shrink-0 flex-col items-center rounded-xl border bg-background p-3 text-center" data-testid={`suggested-card-${u.user_id}`}>
            <button type="button" className="absolute right-1.5 top-1.5 text-muted-foreground hover:text-foreground"
              onClick={() => setUsers((list) => list.map((x) => (x.user_id === u.user_id ? { ...x, dismissed: true } : x)))}
              data-testid={`suggested-dismiss-${u.user_id}`} aria-label="Dismiss">
              <X className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={() => onOpenProfile?.(u.user_id)} className="flex flex-col items-center">
              <Avatar className="h-14 w-14">
                {u.picture ? <AvatarImage src={mediaUrl(u.picture)} /> : null}
                <AvatarFallback>{(u.name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <span className="mt-2 flex max-w-full items-center gap-1 text-sm font-medium">
                <span className="truncate">{u.name}</span>
                {u.is_official && <OfficialBadge className="h-3.5 w-3.5" />}
              </span>
            </button>
            <span className="mt-0.5 line-clamp-2 min-h-[2rem] text-[11px] text-muted-foreground">{reasonText(u, es)}</span>
            <Button size="sm" variant={followed[u.user_id] ? 'outline' : 'default'} className="mt-2 h-7 w-full rounded-full text-xs"
              onClick={() => toggle(u.user_id)} data-testid={`suggested-follow-${u.user_id}`}>
              {followed[u.user_id] ? (es ? 'Siguiendo' : 'Following') : u.reason === 'follows_you' ? (es ? 'Seguir también' : 'Follow back') : (es ? 'Seguir' : 'Follow')}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SuggestedForYou;
